/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useState } from 'react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { createTheme } from '@material-ui/core/styles';
import DateFnsUtils from '@date-io/date-fns';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { KeyboardDatePicker, DatePicker as MuiDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import * as Locales from 'date-fns/locale';
import { ThemeProvider } from '@material-ui/styles';
import { Trans, useTranslation } from 'react-i18next';

import { shortDateUIFormat } from '../../../settings';
import {
  jsDateToLocalISO8601DateString,
  dateStringToLocalDate,
  getMinDate,
  shortFormatDate,
} from '../../../shared/utils/dateUtils';

import { useConfiguration } from '../../../redux/features/configuration/useConfiguration';
import { ConfigResourceProps } from '../../../redux/enums/ConfigResourceProps';
import { useTheming } from '../../../shared/theming/useTheming';
import Tooltip from '../../common/Tooltip';
import { uiSchemaProperties } from '../../../shared/enums/uiSchemaProperties';
import AssistiveText from './AssistiveText';

const useStyles = makeStyles(theme => ({
  root: ({ hasErrors, hasWarnings }) => ({
    '& fieldset': () => ({
      borderColor: `${(hasErrors && theme.palette.error.main) ||
        (hasWarnings && theme.palette.warning.main) ||
        (!hasWarnings && !hasErrors && theme.palette.input)}`,

      border: `${(hasErrors && `2px solid ${theme.palette.error.main}`) ||
        (hasWarnings && `2px solid ${theme.palette.warning.main}`) ||
        (!hasWarnings && !hasErrors && `calc(1rem / 16) solid ${theme.palette.input}`)}`,
    }),
    '& label': {
      transform: 'translate(0.875rem, 1rem) scale(1)',
      color: theme.palette.input,
      textOverflow: 'ellipsis',
      maxWidth: '70%',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    },
    '& label.Mui-error': {
      color: theme.palette.titleText,
    },
    '& .MuiFormLabel-root.Mui-error': {
      color: theme.palette.mainText,
    },
    '& p.Mui-error': {
      color: theme.palette.failed,
      fontSize: '1rem',
    },
    '& div.Mui-error': {
      border: `2px solid ${theme.palette.failed}`,
    },
    '& label.Mui-disabled': {
      color: theme.palette.input,
    },
  }),
  inputAdornment: {
    marginLeft: '-3rem !important',
  },
  wrapper: {
    display: 'flex',
    alignItems: 'center',
  },
}));

export default function DatePicker(props) {
  const {
    id,
    schema,
    uiSchema,
    disableFuture,
    minDate,
    maxDate,
    className,
    fullWidth,
    clearable,
    disabled,
    rawErrors,
    rawHelp,
  } = props;
  const locales = Locales;

  const hasErrors = rawErrors?.length > 0 || uiSchema?.[uiSchemaProperties.UI_HELP_SEVERITY] === 'error';
  const hasWarnings = rawHelp?.length > 0 && uiSchema?.[uiSchemaProperties.UI_HELP_SEVERITY] === 'info';
  const [dateFormatError, setDateFormatError] = useState(null);

  const theming = useTheming();
  const classes = useStyles({ hasErrors, hasWarnings });
  const { t, i18n } = useTranslation();

  const frontendCustomisation = useConfiguration(ConfigResourceProps.FRONTEND_CUSTOMISATION);
  const frontendCustomisationDateFormat = frontendCustomisation.resource?.dateFormat;
  const frontendCustomisationFields = frontendCustomisation.resource?.fields || theming.theme?.fields;
  const tooltip = schema?.config?.tooltip || uiSchema?.[uiSchemaProperties.UI_TOOLTIP];

  const format =
    uiSchema && uiSchema['ui:dateFormat']
      ? uiSchema['ui:dateFormat']
      : frontendCustomisationDateFormat || shortDateUIFormat;
  const isDisabled = uiSchema?.['ui:disabled'] || disabled;
  const isMediumOrBigger = useMediaQuery(theme => theme.breakpoints.up('md'));
  const Picker = isMediumOrBigger ? KeyboardDatePicker : MuiDatePicker;
  const pickerProps = isMediumOrBigger
    ? {
      InputAdornmentProps: {
        classes: {
          root: classes.inputAdornment,
        },
      },
    }
    : {};
  const locale = locales[i18n.language];

  const handleFocusEvent = () => {
    if (props.onFocus) props.onFocus();
  };

  const handleBlurEvent = () => {
    if (props.onBlur) props.onBlur();
  };
  const theme = useTheme();
  const caledarStyleTheme = createTheme({
    overrides: {
      MuiIconButton: {
        root: {
          color: theme.palette.mainText,
          padding: '0 0 0 12px',
        },
      },
      MuiInputLabel: {
        root: {
          whiteSpace: 'nowrap',
          '&.Mui-focused': {
            color: theme.palette.mainText,
            backgroundColor: theme.palette.white,
            transform: 'translate(.875rem, -.375rem) !important',
            paddingLeft: '0.375rem',
            paddingRight: '0.313rem',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          },
          '&[class^="MuiFormLabel-filled"], &[class*=" MuiFormLabel-filled"]': {
            transform: 'translate(0.875rem, -.375rem) scale(1) !important',
            backgroundColor: theme.palette.white,
            paddingLeft: '0.375rem',
            paddingRight: '0.313rem',
            whiteSpace: 'nowrap',
          },
        },
      },
      MuiOutlinedInput: {
        root: {
          '&:focus, &:active, &$focused': {
            boxShadow: `0 0 0 1px #fff, 0 0 0 4px ${theme.palette.focus}`,
          },
          '& fieldset': {
            border: `1px solid ${theme.palette.input} !important`,
          },
          '&.Mui-error fieldset': {
            border: 'none !important',
          },
        },
        input: {
          padding: '.875rem .875rem',
          backgroundColor: theme.palette.white,
          '&:disabled': {
            backgroundColor: theme.palette.backgroundDisabled,
          },
        },
      },
      MuiPickersToolbarButton: {
        toolbarBtn: {
          textTransform: 'capitalize',
        },
      },
      MuiPickersModal: {
        dialogRoot: {
          textTransform: 'capitalize',
        },
      },
      MuiPickersCalendarHeader: {
        transitionContainer: {
          textTransform: 'capitalize',
        },
      },
      MuiFormLabel: {
        asterisk: {
          display: frontendCustomisationFields?.required?.displayAsterisk,
        },
      },
    },
  });

  const onChange = (date) => {
    const result = jsDateToLocalISO8601DateString(date);
    if (result === null && date) {
      setDateFormatError(t('date.format-error'));
    } else {
      setDateFormatError(null);
    }

    props.onChange(result);
    return null;
  };

  const label = document.getElementById(`${id}-label`);
  const [showTooltip, setShowTooltip] = useState(false);

  const onMouseEnterCallback = () => {
    const isOverflowing = label.offsetWidth < label.scrollWidth;
    if (isOverflowing) {
      setShowTooltip(true);
    } else {
      setShowTooltip(false);
    }
  };

  const onMouseExitCallback = () => {
    setShowTooltip(false);
  };

  return (
    <>
      <Tooltip placement="top" open={showTooltip} title={schema?.title ? schema?.title : props.label}>
        <div
          onMouseEnter={onMouseEnterCallback}
          onMouseLeave={onMouseExitCallback}
          className={classes.wrapper}
          aria-label={t('last.modification.date')}
        >
          <MuiPickersUtilsProvider locale={locale} utils={DateFnsUtils}>
            <ThemeProvider theme={caledarStyleTheme}>
              <Picker
                autoOk
                cancelLabel={t('actions.cancel')}
                clearLabel={t('button.clear.label')}
                okLabel={t('actions.ok')}
                clearable={clearable === undefined || clearable}
                fullWidth={fullWidth}
                disableFuture={disableFuture}
                minDate={getMinDate(schema?.maxPastMonths) || minDate}
                minDateMessage={`${t('validation.date.minDate')} ${shortFormatDate(minDate, format)}`}
                maxDateMessage={`${t('validation.date.maxDate')} ${shortFormatDate(maxDate, format)}`}
                inputVariant="outlined"
                format={format}
                placeholder={t(format)}
                id={props.id}
                name={props.id}
                className={`${className} ${classes.root}`}
                label={schema?.title ? schema?.title : props.label}
                // keyboardButtonProps={{ 'aria-label': t('last.modification.date.change') }}
                value={dateStringToLocalDate(props.value)}
                required={props.required}
                onChange={date => onChange(date)}
                onBlur={handleBlurEvent}
                onFocus={handleFocusEvent}
                InputLabelProps={{}}
                data-testid={props.testId}
                disabled={isDisabled}
                {...pickerProps}
              />
            </ThemeProvider>
          </MuiPickersUtilsProvider>
          {tooltip && <Tooltip title={<Trans i18nKey={tooltip} />} />}
        </div>
      </Tooltip>
      {dateFormatError && <AssistiveText severity="warning">{dateFormatError}</AssistiveText>}
    </>
  );
}
